import { API_TUser } from "@/models/auth";
import { Module, Mutation, Action, VuexModule } from "vuex-module-decorators";
import axios from "@/plugins/http";
import { API_ROUTES } from "@/utils/API";
import { REmployee } from "@/pages/Layout/views/Employee/REmployee";
import { REmployer } from "@/pages/Layout/views/Employer/REmployer";
import { RExpert } from "@/pages/Layout/views/Expert/RExpert";
import { RCandidate } from "@/pages/Layout/views/Candidate/RCandidate";
import { formatDateToString } from "@/utils/formatter";
import { CANDIDATE_ROLE, EMPLOYEE_ROLE, EMPLOYER_ROLE, EXPERT_ROLE } from "@/constants/roles";
import { setUserId } from "@amplitude/analytics-browser";
import {
  CompliancePackage,
  CompliancePackageLabel,
  TCompliancePackageLabel,
  SubscriptionStatus,
  API_TSubscriptionStatus,
} from "@/models/company";

@Module({ name: "MUser", namespaced: true })
export default class MUser extends VuexModule {
  user: API_TUser | null = null;

  get isUserSet() {
    return !!this.user;
  }

  @Mutation
  async setUser(payload: API_TUser) {
    this.user = payload;

    // Amplitude
    const userId = payload.ID;

    if (!userId) return;

    setUserId(`amp_id_${userId}`);
  }

  @Mutation
  unsetUser() {
    this.user = null;
  }

  @Action
  async updateCurrentUser() {
    const { data } = await axios.get(API_ROUTES.GET_CURRENT_USER);

    data ? await this.setUser(data) : this.unsetUser();
  }

  get isValidationPassed() {
    return this.user?.has_validation_passed ?? false;
  }

  get currentId() {
    return this.user?.ID ?? 0;
  }

  get companyId() {
    return this.user?.company_id ?? 0;
  }

  get isRoleSet() {
    return typeof this.user?.role === "string";
  }

  get currentRole() {
    return this.user?.role;
  }

  get isEmployer() {
    return this.user?.role === EMPLOYER_ROLE;
  }

  get isEmployee() {
    return this.user?.role === EMPLOYEE_ROLE;
  }

  get isCandidate() {
    return this.user?.role === CANDIDATE_ROLE;
  }

  get isExpert() {
    return this.user?.role === EXPERT_ROLE;
  }

  get basePath() {
    if (this.isEmployer) return `/${REmployer.path}`;
    if (this.isEmployee) return `/${REmployee.path}`;
    if (this.isExpert) return `/${RExpert.path}`;
    if (this.isCandidate) return `/${RCandidate.path}`;

    return "";
  }

  get isSponsor() {
    if (!this.user) return false;

    return this.user.if_sponsor;
  }

  get isSponsorConfirmation() {
    if (!this.user) return false;

    return this.user.sponsor_confirmation;
  }

  get isSponsorConfirmed() {
    return this.isSponsor && this.isSponsorConfirmation;
  }

  get isNotSponsor() {
    return !this.isSponsor || !this.isSponsorConfirmation;
  }

  get isSLStarted() {
    if (!this.user) return false;

    return this.user.is_sl_started;
  }

  get slIsNotOnFirstStep() {
    if (!this.user) return false;

    return this.user.sl_is_not_on_first_step;
  }

  get hasSLCase(): boolean {
    return !!this.user?.sl_case_id;
  }

  // Packages

  // Blocked Compliance

  get isComplianceBlocked() {
    if (!this.user) return false;

    return this.user.is_compliance_blocked;
  }

  // Subscription Package Label: Trial | Basic | Standard | Premium

  get compliancePackageLabel(): TCompliancePackageLabel {
    const compliancePackage = this.user?.company?.compliance_package;

    if (compliancePackage === CompliancePackage.Basic) return CompliancePackageLabel.Basic;

    if (compliancePackage === CompliancePackage.BasicPlus) return CompliancePackageLabel.Standard;

    if (compliancePackage === CompliancePackage.Premium) return CompliancePackageLabel.Premium;

    return CompliancePackageLabel.Trial;
  }

  // Subscription Status: Trial | Active | Blocked

  get subscriptionStatus(): API_TSubscriptionStatus {
    return this.user?.company?.subscription_status || SubscriptionStatus.Trial;
  }

  get isSubscriptionStatusTrial(): boolean {
    return this.subscriptionStatus === SubscriptionStatus.Trial;
  }

  get isSubscriptionStatusActive(): boolean {
    return this.subscriptionStatus === SubscriptionStatus.Active;
  }

  get isSubscriptionStatusBlocked(): boolean {
    return this.subscriptionStatus === SubscriptionStatus.Blocked;
  }

  // Subscription Package: Basic | Standard | Premium

  get isBasicSubscription() {
    return this.compliancePackageLabel === CompliancePackageLabel.Basic;
  }

  get isStandardSubscription() {
    return this.compliancePackageLabel === CompliancePackageLabel.Standard;
  }

  get isPremiumSubscription() {
    return this.compliancePackageLabel === CompliancePackageLabel.Premium;
  }

  // Is Subscriprion Active

  get isBasicSubscriptionActive() {
    return this.isBasicSubscription && this.isSubscriptionStatusActive;
  }

  get isStandardSubscriptionActive() {
    return this.isStandardSubscription && this.isSubscriptionStatusActive;
  }

  get isPremiumSubscriptionActive() {
    return this.isPremiumSubscription && this.isSubscriptionStatusActive;
  }

  get isComplianceSubscriptionActive() {
    return (
      this.isBasicSubscriptionActive ||
      this.isStandardSubscriptionActive ||
      this.isPremiumSubscriptionActive
    );
  }

  // Compliance Subscriprion Expiration Dates

  get complianceSubscriptionExpitationDate() {
    return this.user?.company?.subscription_end_date || "";
  }

  get complianceTrialExpitationDate() {
    return this.user?.company?.trial_date_end || "";
  }

  get complianceExpitationDate() {
    if (this.isSubscriptionStatusActive)
      return formatDateToString(this.complianceSubscriptionExpitationDate);

    return formatDateToString(this.complianceTrialExpitationDate);
  }

  // Prepaid Services

  get prepaidCosSW(): number {
    return this.user?.prepaid_services_details?.cos_skilled_worker || 0;
  }

  get prepaidSL(): boolean {
    return this.user?.prepaid_services_details?.sponsor_licence || false;
  }
}
